import { Replay } from '@mui/icons-material';
import dayjs from 'dayjs';
import React from 'react';

import CAutocomplete from '../../../components/CAutocomplete';
import CButton from '../../../components/CButton';
import CLocalizedTextString from '../../../components/CLocalizedTextString';
import GenericDateRangeSelector from '../../../components/GenericDateRangeSelector';
import { BreakpointsInPx } from '../../../enums/BreakpointsInPx';
import { useAppSelector } from '../../../hooks/hooks';
import { type ISectorPerformanceParameters } from '../../../models/ISectorPerformanceParameters';

type PageParameterSubset = Pick<
  ISectorPerformanceParameters,
  'noCompanies' | 'startDate' | 'endDate' | 'selection' | 'interval' | 'weight'
>;

export interface PageParameterSectionProps extends PageParameterSubset {
  onParamChange: (updated: Partial<ISectorPerformanceParameters>) => void;
  onReset: () => void;
  maxDate: string,
  minDate: string,
}

export default function PageParameterSection(props: PageParameterSectionProps) {

  const { dashboardOutletDimensions } = useAppSelector((state) => state.dashboard);
  const isMobile = dashboardOutletDimensions.w <= BreakpointsInPx.BREAKPOINT_3;
  const gridTemplate = isMobile ? 'repeat(1, minmax(0, 1fr))' : 'repeat(4, minmax(0, 1fr)) max-content';

  const spanCol = isMobile ? 1 : 2;
  const companiesTextField = CLocalizedTextString('sectorPerformance.textField.companies');
  const weightTextField = CLocalizedTextString('sectorPerformance.textField.weight');
  const weightOption1 = CLocalizedTextString('sectorPerformance.textField.weight.option1');
  const weightOption2 = CLocalizedTextString('sectorPerformance.textField.weight.option2');


  const resetButton = <CButton icons={{ start: <Replay /> }} onClick={props.onReset} isForm style={{borderWidth: 1}} />;
  return (
    <div className="flex flex-col gap-2">
      <div className={`flex gap-x-2 gap-y-4 ${isMobile ? 'flex-col items-center' : 'flex-row'}`}>
        <div className="grid gap-2 w-full" style={{ gridTemplateColumns: gridTemplate }}>
          <div className={`flex gap-2 col-span-${spanCol}`}>
            <div className="grid gap-2 grid-cols-2 w-full">
              <CAutocomplete
                textFieldLabel={companiesTextField}
                options={[
                  { key: '500', label: '500' },
                  { key: '1000', label: '1000' },
                  { key: '1500', label: '1500' }
                ]}
                value={
                  props.noCompanies
                    ? [{ key: String(props.noCompanies), label: String(props.noCompanies) }]
                    : []
                }
                onChangeSingle={(opt) => {
                  props.onParamChange({
                    noCompanies: Number(opt.key) as ISectorPerformanceParameters['noCompanies']
                  });
                }}
              />

              <CAutocomplete
                textFieldLabel={weightTextField}
                options={[
                  { key: 'mcap', label: weightOption1 },
                  { key: 'equal', label: weightOption2 }
                ]}
                value={[{ key: props.weight, label: props.weight === 'mcap' ? weightOption1 : weightOption2 }]}
                onChangeSingle={(opt) => {
                  props.onParamChange({
                    weight: opt.key as ISectorPerformanceParameters['weight'],
                  });
                }}
              />
            </div>
            {isMobile && resetButton}
          </div>

          <div className={`col-span-${spanCol}`}>
            <GenericDateRangeSelector
              minDate={dayjs(props.minDate).valueOf()}
              maxDate={dayjs(props.maxDate).valueOf()}
              initStartDate={dayjs(props.startDate).valueOf()}
              initEndDate={dayjs(props.endDate).valueOf()}
              inlineScroll
              onValidationErrorDeprecate={() => {
              }}
              onChangeDeprecate={({ start, end }) => {
                if (start) props.onParamChange({ startDate: dayjs(start).format('YYYY-MM-DD') });
                if (end) props.onParamChange({ endDate: dayjs(end).format('YYYY-MM-DD') });
              }}
            />
          </div>
          {!isMobile && <div>{resetButton}</div>}
        </div>
      </div>
    </div>
  );
}
